import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TouchableOpacity,
  ActivityIndicator,
  Platform,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { ScreenLayout, Header, FormHeader } from '../components';
import { ProductList } from '../components/products';
import { ChannelBindingModal } from '../components/channels';
import { apiService } from '../services/api';
import { useAppContext } from '../contexts/AppContext';
import { createTabPressHandler } from '../navigation/handleTabPress';
import { colors, spacing, typography } from '../theme';
import { designTokens } from '../theme/design-system';
import { useThemedStyles } from '../hooks/useThemedStyles';
import { businessCurrency } from '../utils/businessDefaults';

interface CatalogueDetailScreenProps {
  catalogueId: string;
}

export const CatalogueDetailScreen: React.FC<CatalogueDetailScreenProps> = ({
  catalogueId,
}) => {
  const styles = useThemedStyles(getStyles);
  const { navigateTo, showNotification, activeTab, setActiveTab } = useAppContext();
  const [catalogue, setCatalogue] = useState<any>(null);
  const [statistics, setStatistics] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [loadingStats, setLoadingStats] = useState(false);
  const [products, setProducts] = useState<any[]>([]);
  const [productsLoading, setProductsLoading] = useState(false);
  const [showChannelBindingModal, setShowChannelBindingModal] = useState(false);
  const [availableChannels, setAvailableChannels] = useState<any[]>([]);
  const [channelBindings, setChannelBindings] = useState<any[]>([]);
  const [loadingChannels, setLoadingChannels] = useState(false);

  useEffect(() => {
    loadCatalogue();
    loadStatistics();
    loadProducts();
  }, [catalogueId]);

  useEffect(() => {
    if (showChannelBindingModal) {
      loadChannelsData(); // Reload bindings when modal opens to get latest sync status
    }
  }, [showChannelBindingModal]);
  
  // Also reload bindings periodically or after sync operations
  useEffect(() => {
    // Reload bindings when catalogue changes
    if (catalogueId) {
      loadChannelsData();
    }
  }, [catalogueId]);

  const loadCatalogue = async () => {
    try {
      setLoading(true);
      const response = await apiService.getCatalogueById(catalogueId);
      if (response.statusCode === 1000 && response.data) {
        setCatalogue(response.data);
      } else {
        showNotification('error', 'Error', 'Failed to load catalogue');
        setTimeout(() => {
          if (Platform.OS === 'web') {
            window.history.back();
          } else {
            navigateTo('catalogues');
          }
        }, 2000);
      }
    } catch (error: any) {
      console.error('Error loading catalogue:', error);
      showNotification('error', 'Error', error.message || 'Failed to load catalogue');
      setTimeout(() => {
        if (Platform.OS === 'web') {
          window.history.back();
        } else {
          navigateTo('catalogues');
        }
      }, 2000);
    } finally {
      setLoading(false);
    }
  };

  const loadStatistics = async () => {
    try {
      setLoadingStats(true);
      const response = await apiService.getCatalogueStatistics(catalogueId);
      if (response.statusCode === 1000 && response.data) {
        setStatistics(response.data);
      }
    } catch (error: any) {
      console.error('Error loading statistics:', error);
    } finally {
      setLoadingStats(false);
    }
  };

  const loadProducts = async (page: number = 0, reset: boolean = false) => {
    try {
      if (reset) {
        setProductsLoading(true);
      }

      const response = await apiService.getProductsByCatalogue(catalogueId, {
        limit: 20,
        skip: page * 20,
      });

      if (response.statusCode === 1000 && response.data) {
        const newProducts = response.data.products || [];
        setProducts((prev) => (reset ? newProducts : [...prev, ...newProducts]));
      }
    } catch (error: any) {
      console.error('Error loading products:', error);
    } finally {
      setProductsLoading(false);
    }
  };

  const handleEdit = () => {
    navigateTo('catalogue-edit', catalogueId);
  };

  const handleAddProduct = () => {
    // Navigate to product create with this catalogue ID
    if (Platform.OS === 'web') {
      navigateTo('product-create', catalogueId);
    } else {
      navigateTo('product-create', catalogueId);
    }
  };

  const handleProductPress = (productId: string) => {
    navigateTo('product-detail', productId);
  };

  const handleProductEdit = (productId: string) => {
    navigateTo('product-edit', productId);
  };

  const handleProductDelete = async (productId: string) => {
    try {
      const response = await apiService.removeProductFromCatalogue(catalogueId, productId);
      if (response.statusCode === 1000) {
        showNotification('success', 'Success', 'Product removed from catalogue successfully');
        loadProducts(0, true);
        loadStatistics(); // Reload statistics to update product count
      } else {
        showNotification('error', 'Error', response.message || 'Failed to remove product from catalogue');
      }
    } catch (error: any) {
      console.error('Error removing product from catalogue:', error);
      showNotification('error', 'Error', error.message || 'Failed to remove product from catalogue');
    }
  };

  const loadChannelsData = async () => {
    try {
      setLoadingChannels(true);
      const [channelsResponse, bindingsResponse] = await Promise.all([
        apiService.getAvailableChannels(),
        apiService.getCatalogueChannelBindings(catalogueId),
      ]);

      if (channelsResponse.statusCode === 1000 && channelsResponse.data) {
        setAvailableChannels(channelsResponse.data || []);
      }

      if (bindingsResponse.statusCode === 1000 && bindingsResponse.data) {
        const bindings = Array.isArray(bindingsResponse.data) ? bindingsResponse.data : [];
        setChannelBindings(bindings);
        console.log('[CatalogueDetailScreen] Loaded channel bindings:', bindings.map((b: any) => ({
          channel: b.channel,
          syncStatus: b.syncStatus,
          externalCatalogId: b.externalCatalogId ? 'exists' : 'missing',
          lastSyncedAt: b.lastSyncedAt,
        })));
      }
    } catch (error: any) {
      console.error('Error loading channels data:', error);
      showNotification('error', 'Error', 'Failed to load channels data');
    } finally {
      setLoadingChannels(false);
    }
  };

  const handleSyncToWhatsApp = async () => {
    try {
      showNotification('info', 'Syncing', 'Syncing catalogue to WhatsApp...');
      const syncResponse = await apiService.syncCatalogueToWhatsApp(catalogueId);
      
      if (syncResponse.statusCode === 1000) {
        const { productsSynced, productsFailed } = syncResponse.data?.syncResult || {};
        if (productsFailed === 0) {
          showNotification('success', 'Success', `Catalogue synced successfully. ${productsSynced || 0} products synced to WhatsApp.`);
        } else {
          showNotification('warning', 'Partial Success', `Catalogue synced with ${productsFailed} failures. ${productsSynced || 0} products synced.`);
        }
        await loadChannelsData(); // Reload to update sync status
        await loadStatistics(); // Reload statistics
      } else {
        showNotification('error', 'Error', syncResponse.message || 'Failed to sync catalogue to WhatsApp');
        await loadChannelsData(); // Still reload to show current status
      }
    } catch (error: any) {
      console.error('Error syncing to WhatsApp:', error);
      showNotification('error', 'Error', error.message || 'Failed to sync catalogue to WhatsApp');
      await loadChannelsData(); // Still reload to show current status
    }
  };

  const handleBulkBind = async (channels: string[]) => {
    try {
      // Get currently bound channels
      const currentChannels = channelBindings.map(b => b.channel);
      
      // Channels to bind (new ones)
      const toBind = channels.filter(ch => !currentChannels.includes(ch));
      
      // Channels to unbind (removed ones)
      const toUnbind = currentChannels.filter(ch => !channels.includes(ch));

      // Bind new channels
      for (const channel of toBind) {
        await apiService.bindCatalogueToChannel(catalogueId, channel);
      }

      // Unbind removed channels
      for (const channel of toUnbind) {
        await apiService.unbindCatalogueFromChannel(catalogueId, channel);
      }

      // Reload to get updated bindings
      await loadChannelsData();

      // Auto-sync to WhatsApp after binding (if WhatsApp is in the newly bound channels)
      if (toBind.includes('whatsapp')) {
        try {
          showNotification('info', 'Syncing', 'Syncing catalogue to WhatsApp...');
          const syncResponse = await apiService.syncCatalogueToWhatsApp(catalogueId);
          
          if (syncResponse.statusCode === 1000) {
            const { productsSynced, productsFailed } = syncResponse.data?.syncResult || {};
            if (productsFailed === 0) {
              showNotification('success', 'Success', `Catalogue synced successfully. ${productsSynced || 0} products synced to WhatsApp.`);
            } else {
              showNotification('warning', 'Partial Success', `Catalogue synced with ${productsFailed} failures. ${productsSynced || 0} products synced.`);
            }
            await loadChannelsData(); // Reload to update sync status
          } else {
            showNotification('warning', 'Warning', 'Channels updated but sync failed. You can sync manually later.');
            await loadChannelsData(); // Still reload to show current status
          }
        } catch (syncError: any) {
          console.error('Error syncing to WhatsApp:', syncError);
          showNotification('warning', 'Warning', 'Channels updated but sync failed. You can sync manually later.');
          await loadChannelsData(); // Still reload to show current status
        }
      } else {
        showNotification('success', 'Success', 'Channels updated successfully');
        await loadChannelsData(); // Reload to show updated bindings
      }
    } catch (error: any) {
      showNotification('error', 'Error', error.message || 'Failed to update channel bindings');
      throw error;
    }
  };

  const handleTabPress = createTabPressHandler(navigateTo, setActiveTab);

  if (loading) {
    return (
      <ScreenLayout
        disableScreenPadding
        header={
          <Header
            showNotifications={true}
            showMessages={true}
            onNotificationPress={() => navigateTo('notifications')}
            onMessagePress={() => navigateTo('chat')}
            showNavigation={Platform.OS === 'web'}
            activeTab={activeTab}
            onTabPress={handleTabPress}
          />
        }
        showBottomTabs={Platform.OS !== 'web'}
        onLeftPaneClose={() => {}}
        leftPaneContent="settings"
        onSettingsItemPress={async (itemId) => {
          const { setPendingSettingsItem } = await import('../utils/tempStorage');
          await setPendingSettingsItem(itemId);
          navigateTo('settings');
        }}
        onRightPaneClose={() => {}}
        formHeader={
          <FormHeader
            title="Catalogue Details"
            parentScreenPaddingDisabled
            onBack={() => {
              if (Platform.OS === 'web') {
                window.history.back();
              } else {
                navigateTo('catalogues');
              }
            }}
          />
        }
      >
        <View style={styles.loadingContainer}>
          <ActivityIndicator size="large" color={colors.primary} />
          <Text style={styles.loadingText}>Loading catalogue...</Text>
        </View>
      </ScreenLayout>
    );
  }

  if (!catalogue) {
    return null;
  }

  return (
    <ScreenLayout
      disableScreenPadding
      header={
        <Header
          showNotifications={true}
          showMessages={true}
          onNotificationPress={() => navigateTo('notifications')}
          onMessagePress={() => navigateTo('chat')}
          showNavigation={Platform.OS === 'web'}
          activeTab={activeTab}
          onTabPress={handleTabPress}
        />
      }
      showBottomTabs={Platform.OS !== 'web'}
      onLeftPaneClose={() => {}}
      leftPaneContent="settings"
      onSettingsItemPress={async (itemId) => {
        const { setPendingSettingsItem } = await import('../utils/tempStorage');
        await setPendingSettingsItem(itemId);
        navigateTo('settings');
      }}
      onRightPaneClose={() => {}}
      formHeader={
        <FormHeader
          title={catalogue.name}
          parentScreenPaddingDisabled
          onBack={() => {
            if (Platform.OS === 'web') {
              window.history.back();
            } else {
              navigateTo('catalogues');
            }
          }}
          actions={[{ icon: 'create-outline', onPress: handleEdit, accessibilityLabel: 'Edit catalogue' }]}
        />
      }
    >
      <View style={styles.screen}>
        <ScrollView
          style={styles.container}
          contentContainerStyle={[
            styles.contentContainer,
            Platform.OS !== 'web' && styles.contentContainerMobile,
          ]}
          showsVerticalScrollIndicator={false}
        >
        {/* Catalogue Info */}
        <View style={styles.infoCard}>
          <View style={styles.infoHeader}>
            <View style={styles.iconContainer}>
              <Ionicons name="cube" size={32} color={colors.primary} />
            </View>
            <View style={styles.infoContent}>
              <View style={styles.nameRow}>
                <View style={styles.nameContainer}>
                  <Text style={styles.name} numberOfLines={2}>{catalogue.name}</Text>
                  {catalogue.description && (
                    <Text style={styles.description} numberOfLines={2}>{catalogue.description}</Text>
                  )}
                </View>
                <View style={styles.actionsContainer}>
                  <TouchableOpacity onPress={handleEdit} style={styles.editButton}>
                    <Ionicons name="create-outline" size={20} color={colors.primary} />
                  </TouchableOpacity>
                  {catalogue.isActive === false && (
                    <View style={styles.inactiveBadge}>
                      <Text style={styles.inactiveBadgeText}>Inactive</Text>
                    </View>
                  )}
                </View>
              </View>
            </View>
          </View>

          {/* Statistics */}
          {statistics && (
            <View style={styles.statsContainer}>
              <View style={styles.statCard}>
                <Ionicons name="cube" size={24} color={colors.primary} />
                <Text style={styles.statValue}>{statistics.productCount || 0}</Text>
                <Text style={styles.statLabel}>Products</Text>
              </View>
              <View style={styles.statCard}>
                <Ionicons name="checkmark-circle" size={24} color={colors.success} />
                <Text style={styles.statValue}>{statistics.activeProductCount || 0}</Text>
                <Text style={styles.statLabel}>In Stock</Text>
              </View>
              {statistics.lowStockProductCount > 0 && (
                <View style={styles.statCard}>
                  <Ionicons name="warning" size={24} color={colors.warning} />
                  <Text style={styles.statValue}>{statistics.lowStockProductCount}</Text>
                  <Text style={styles.statLabel}>Low Stock</Text>
                </View>
              )}
            </View>
          )}

          {/* Settings */}
          <View style={styles.settingsRow}>
            <View style={styles.settingItem}>
              <Text style={styles.settingLabel}>Currency</Text>
              <Text style={styles.settingValue}>{catalogue.currency || businessCurrency()}</Text>
            </View>
            <View style={styles.settingItem}>
              <Text style={styles.settingLabel}>Locale</Text>
              <Text style={styles.settingValue}>{catalogue.locale || 'en_US'}</Text>
            </View>
          </View>
        </View>

        {/* Products Section */}
        <View style={styles.productsSection}>
          <View style={styles.sectionHeader}>
            <Text style={styles.sectionTitle}>Products</Text>
            <View style={styles.headerActions}>
              <TouchableOpacity
                style={[styles.iconButton, styles.existingProductButton]}
                onPress={() => navigateTo('add-products-to-catalogue', catalogueId)}
                activeOpacity={0.7}
                hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
              >
                <Ionicons name="list-outline" size={24} color={colors.secondary} />
              </TouchableOpacity>
              <TouchableOpacity
                style={[styles.iconButton, styles.newProductButton]}
                onPress={handleAddProduct}
                activeOpacity={0.7}
                hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
              >
                <Ionicons name="add-circle" size={24} color={colors.primary} />
              </TouchableOpacity>
              <TouchableOpacity
                style={styles.iconButton}
                onPress={() => setShowChannelBindingModal(true)}
                activeOpacity={0.7}
                hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
              >
                <Ionicons name="link-outline" size={24} color={colors.primary} />
              </TouchableOpacity>
              {/* Sync button - only show if WhatsApp is bound */}
              {channelBindings.some(b => b.channel === 'whatsapp') && (
                <TouchableOpacity
                  style={styles.iconButton}
                  onPress={handleSyncToWhatsApp}
                  activeOpacity={0.7}
                  hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
                >
                  <Ionicons name="sync-outline" size={24} color={colors.primary} />
                </TouchableOpacity>
              )}
            </View>
          </View>

          <ProductList
            products={products}
            loading={productsLoading}
            onProductPress={handleProductPress}
            onProductEdit={handleProductEdit}
            onProductDelete={handleProductDelete}
            showActions={true}
            emptyMessage="No products in this catalogue yet. Add your first product!"
          />
        </View>
      </ScrollView>

      <ChannelBindingModal
        visible={showChannelBindingModal}
        onClose={() => setShowChannelBindingModal(false)}
        onConfirm={handleBulkBind}
        availableChannels={availableChannels}
        currentBindings={channelBindings}
        catalogueName={catalogue?.name}
        loading={loadingChannels}
      />
      </View>
    </ScreenLayout>
  );
};

const getStyles = () => StyleSheet.create({
  screen: {
    flex: 1,
  },
  container: {
    flex: 1,
    backgroundColor: colors.lightGray,
  },
  contentContainer: {
    padding: spacing.lg,
    paddingBottom: spacing.xl,
  },
  contentContainerMobile: {
    paddingBottom: 100,
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  loadingText: {
    marginTop: spacing.md,
    fontSize: typography.fontSize.base,
    color: colors.textLight,
    fontFamily: typography.fontFamily.primary,
  },
  infoCard: {
    backgroundColor: colors.background.surface,
    borderRadius: designTokens.borderRadius.lg,
    padding: spacing.lg,
    marginBottom: spacing.lg,
    ...designTokens.shadows.sm,
  },
  infoHeader: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    marginBottom: spacing.md,
  },
  iconContainer: {
    width: 56,
    height: 56,
    borderRadius: designTokens.borderRadius.md,
    backgroundColor: colors.primary + '15',
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: spacing.md,
  },
  infoContent: {
    flex: 1,
  },
  nameRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    marginBottom: spacing.xs,
    gap: spacing.sm,
  },
  nameContainer: {
    flex: 1,
    minWidth: 0,
  },
  actionsContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.xs,
  },
  name: {
    fontSize: typography.fontSize['2xl'],
    fontWeight: '700',
    color: colors.textDark,
    fontFamily: typography.fontFamily.bold,
  },
  editButton: {
    padding: spacing.xs,
  },
  description: {
    fontSize: typography.fontSize.base,
    color: colors.textLight,
    fontFamily: typography.fontFamily.primary,
    lineHeight: 22,
  },
  inactiveBadge: {
    backgroundColor: colors.warning + '20',
    paddingHorizontal: spacing.sm,
    paddingVertical: spacing.xs,
    borderRadius: designTokens.borderRadius.sm,
    alignSelf: 'flex-start',
  },
  inactiveBadgeText: {
    fontSize: typography.fontSize.xs,
    color: colors.warning,
    fontFamily: typography.fontFamily.medium,
  },
  statsContainer: {
    flexDirection: 'row',
    gap: spacing.md,
    marginTop: spacing.md,
    paddingTop: spacing.md,
    borderTopWidth: 1,
    borderTopColor: colors.border + '40',
  },
  statCard: {
    flex: 1,
    alignItems: 'center',
    padding: spacing.md,
    backgroundColor: colors.lightGray,
    borderRadius: designTokens.borderRadius.md,
  },
  statValue: {
    fontSize: typography.fontSize['2xl'],
    fontWeight: '700',
    color: colors.textDark,
    fontFamily: typography.fontFamily.bold,
    marginTop: spacing.xs,
  },
  statLabel: {
    fontSize: typography.fontSize.xs,
    color: colors.textLight,
    fontFamily: typography.fontFamily.primary,
    marginTop: spacing.xs / 2,
  },
  settingsRow: {
    flexDirection: 'row',
    gap: spacing.md,
    marginTop: spacing.md,
    paddingTop: spacing.md,
    borderTopWidth: 1,
    borderTopColor: colors.border + '40',
  },
  settingItem: {
    flex: 1,
  },
  settingLabel: {
    fontSize: typography.fontSize.xs,
    color: colors.textLight,
    fontFamily: typography.fontFamily.primary,
    marginBottom: spacing.xs / 2,
  },
  settingValue: {
    fontSize: typography.fontSize.base,
    fontWeight: '600',
    color: colors.textDark,
    fontFamily: typography.fontFamily.semibold,
  },
  productsSection: {
    marginTop: spacing.md,
  },
  sectionHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: spacing.md,
  },
  sectionTitle: {
    fontSize: typography.fontSize.xl,
    fontWeight: '700',
    color: colors.textDark,
    fontFamily: typography.fontFamily.bold,
  },
  headerActions: {
    flexDirection: 'row',
    gap: spacing.sm,
    alignItems: 'center',
  },
  iconButton: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: colors.primary + '10',
    justifyContent: 'center',
    alignItems: 'center',
  },
  existingProductButton: {
    backgroundColor: colors.secondary + '15',
  },
  newProductButton: {
    backgroundColor: colors.primary + '15',
  },
});

